import { __exportAll } from "./_virtual/_rolldown/runtime.js";
import { getHttpServiceUrlConfig } from "./bite_service_url.js";
import { BiteServiceName, BiteServicePorts, Environment, EnvironmentHelper } from "@biteinc/enums";
//#region src/bite_url.ts
var bite_url_exports = /* @__PURE__ */ __exportAll({
	biteApiUrl: () => biteApiUrl,
	biteAssetUrlWithNewHost: () => biteAssetUrlWithNewHost,
	biteReceiptUrl: () => biteReceiptUrl,
	bureauUrl: () => bureauUrl,
	internalServiceUrl: () => internalServiceUrl,
	mcpBaseUrl: () => mcpBaseUrl,
	recommendationServiceUrl: () => recommendationServiceUrl,
	resolveDeploymentServiceUrl: () => resolveDeploymentServiceUrl,
	serviceUrl: () => serviceUrl,
	webhookReceiverBaseUrl: () => webhookReceiverBaseUrl
});
const DEPLOYMENT_SERVICE_URL_ENV_VAR_BY_NAME = {
	maitred: "MAITRED_URL",
	garcon: "GARCON_URL",
	vitrine: "VITRINE_URL"
};
/**
* Resolve an opted-in hosted service URL before preview or shared-host routing.
* The override is limited to hosted environments so local URL behavior stays unchanged.
*/
function resolveDeploymentServiceUrl(service, env) {
	if (!EnvironmentHelper.isHosting(env) && !EnvironmentHelper.isPreview()) return;
	const envVar = DEPLOYMENT_SERVICE_URL_ENV_VAR_BY_NAME[service];
	const configuredUrl = getEnvVar(envVar);
	if (!configuredUrl) return;
	return parseDeploymentServiceUrl(configuredUrl, envVar);
}
function parseDeploymentServiceUrl(configuredUrl, envVar) {
	if (configuredUrl.trim() !== configuredUrl) throw new Error(`${envVar} must be an http or https origin without whitespace`);
	let parsedUrl;
	try {
		parsedUrl = new URL(configuredUrl);
	} catch {
		throw new Error(`${envVar} must be a valid http or https origin`);
	}
	const authorityStartIndex = configuredUrl.indexOf("://") + 3;
	const authority = configuredUrl.slice(authorityStartIndex);
	const suffixIndex = authority.search(/[/?#]/u);
	const suffix = suffixIndex === -1 ? "" : authority.slice(suffixIndex);
	if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:" || authority.includes("@") || configuredUrl.includes("\\") || parsedUrl.username !== "" || parsedUrl.password !== "" || parsedUrl.search !== "" || parsedUrl.hash !== "" || parsedUrl.pathname !== "/" || suffix !== "" && suffix !== "/") throw new Error(`${envVar} must be an http or https origin without a path, query, or fragment`);
	return parsedUrl.origin;
}
function biteApiUrl(env) {
	const deploymentServiceUrl = resolveDeploymentServiceUrl("maitred", env);
	if (deploymentServiceUrl !== void 0) return deploymentServiceUrl;
	if (EnvironmentHelper.isPreview()) return `https://pr-${EnvironmentHelper.porterPrNumber()}-maitred.getbite.net`;
	switch (env) {
		case Environment.PROD: return "https://api.getbite.com";
		case Environment.STAGING: return "https://api-staging.getbite.com";
		case Environment.SANDBOX: return "https://api-sandbox.getbite.com";
		case Environment.DEV: {
			const ngrokHost = getEnvVar("NGROK_MAITRED_HOST");
			if (ngrokHost) return `https://${ngrokHost}`;
			const override = getEnvVar("MAITRED_URL");
			if (override) return override;
			return `http://${getIpAddress()}:${BiteServicePorts.Maitred}`;
		}
		case Environment.TEST: {
			const override = getEnvVar("MAITRED_URL");
			if (override) return override;
			return `http://${getIpAddress()}:${BiteServicePorts.Maitred}`;
		}
	}
}
function recommendationServiceUrl(env) {
	switch (env) {
		case Environment.PROD:
		case Environment.STAGING:
		case Environment.SANDBOX: return biteApiUrl(env);
		case Environment.TEST:
		case Environment.DEV: {
			const override = getEnvVar("RECOMMENDATION_APP_URL");
			if (override) return override;
			return `http://${getIpAddress()}:${BiteServicePorts.RecommendationApp}`;
		}
	}
}
/**
* Public base URL of the standalone MCP app (`packages/mcp-app`). This is the
* OAuth *resource* identity clients see, so it must be the host that actually
* serves `/mcp` — maitred still serves its own `/mcp` during the dual run.
*/
function mcpBaseUrl(env) {
	if (EnvironmentHelper.isPreview()) return `https://pr-${EnvironmentHelper.porterPrNumber()}-mcp-app.getbite.net`;
	switch (env) {
		case Environment.PROD: return "https://mcp.getbite.com";
		case Environment.STAGING: return "https://mcp-staging.getbite.com";
		case Environment.SANDBOX: return "https://mcp-sandbox.getbite.com";
		case Environment.DEV:
		case Environment.TEST: {
			const override = getEnvVar("MCP_APP_URL");
			if (override) return override;
			return `http://${getIpAddress()}:${BiteServicePorts.McpApp}`;
		}
	}
}
function biteReceiptUrl(env) {
	switch (env) {
		case Environment.PROD: return "https://txt.bitekiosk.io";
		case Environment.STAGING:
		case Environment.SANDBOX:
		case Environment.TEST:
		case Environment.DEV: return bureauUrl(env);
	}
}
function serviceUrl(env, serviceName) {
	if (EnvironmentHelper.isPreview()) {
		const pr = EnvironmentHelper.porterPrNumber();
		switch (serviceName) {
			case BiteServiceName.RecommendationApp: return `https://pr-${pr}-recommendation.getbite.net`;
			default: throw new Error(`Service ${serviceName} is not available in preview`);
		}
	}
	switch (env) {
		case Environment.PROD: switch (serviceName) {
			case BiteServiceName.RecommendationApp: return "http://recommendation.getbite.com";
			case BiteServiceName.Bureau:
			case BiteServiceName.BuildAndDeploy:
			case BiteServiceName.Clock:
			case BiteServiceName.DbCollectionWatcher:
			case BiteServiceName.Eclair:
			case BiteServiceName.Flash:
			case BiteServiceName.Garcon:
			case BiteServiceName.Maitred:
			case BiteServiceName.McpApp:
			case BiteServiceName.JobWorker:
			case BiteServiceName.Scripts:
			case BiteServiceName.QueueConsumerInternal:
			case BiteServiceName.QueueConsumerOperational:
			case BiteServiceName.QueueConsumerOrders:
			case BiteServiceName.QueueConsumerQuicksand:
			case BiteServiceName.QueueConsumerQuicksandSmall:
			case BiteServiceName.QueueConsumerWebhooks:
			case BiteServiceName.Vitrine:
			case BiteServiceName.WebhookReceiver: throw new Error(`Service ${serviceName} is not a public service`);
			default: throw new Error(`Unknown service name ${serviceName}`);
		}
		case Environment.STAGING: switch (serviceName) {
			case BiteServiceName.RecommendationApp: return "http://recommendation-staging.getbite.com";
			case BiteServiceName.Bureau:
			case BiteServiceName.BuildAndDeploy:
			case BiteServiceName.Clock:
			case BiteServiceName.DbCollectionWatcher:
			case BiteServiceName.Eclair:
			case BiteServiceName.Flash:
			case BiteServiceName.Garcon:
			case BiteServiceName.Maitred:
			case BiteServiceName.McpApp:
			case BiteServiceName.JobWorker:
			case BiteServiceName.Scripts:
			case BiteServiceName.QueueConsumerInternal:
			case BiteServiceName.QueueConsumerOperational:
			case BiteServiceName.QueueConsumerOrders:
			case BiteServiceName.QueueConsumerQuicksand:
			case BiteServiceName.QueueConsumerQuicksandSmall:
			case BiteServiceName.QueueConsumerWebhooks:
			case BiteServiceName.Vitrine:
			case BiteServiceName.WebhookReceiver: throw new Error(`Service ${serviceName} is not a public service`);
			default: throw new Error(`Unknown service name ${serviceName}`);
		}
		case Environment.SANDBOX: switch (serviceName) {
			case BiteServiceName.RecommendationApp: return "http://recommendation-sandbox.getbite.com";
			case BiteServiceName.Bureau:
			case BiteServiceName.BuildAndDeploy:
			case BiteServiceName.Clock:
			case BiteServiceName.DbCollectionWatcher:
			case BiteServiceName.Eclair:
			case BiteServiceName.Flash:
			case BiteServiceName.Garcon:
			case BiteServiceName.Maitred:
			case BiteServiceName.McpApp:
			case BiteServiceName.JobWorker:
			case BiteServiceName.Scripts:
			case BiteServiceName.QueueConsumerInternal:
			case BiteServiceName.QueueConsumerOperational:
			case BiteServiceName.QueueConsumerOrders:
			case BiteServiceName.QueueConsumerQuicksand:
			case BiteServiceName.QueueConsumerQuicksandSmall:
			case BiteServiceName.QueueConsumerWebhooks:
			case BiteServiceName.Vitrine:
			case BiteServiceName.WebhookReceiver: throw new Error(`Service ${serviceName} is not a public service`);
			default: throw new Error(`Unknown service name ${serviceName}`);
		}
		case Environment.DEV:
		case Environment.TEST: return `127.0.0.1`;
	}
}
function internalServiceUrl(env, serviceName) {
	const serviceConfig = getHttpServiceUrlConfig(serviceName);
	if (serviceConfig === void 0) return;
	const internalOverride = getEnvVar(serviceConfig.internalUrlEnvVar);
	if (internalOverride) return internalOverride;
	if (EnvironmentHelper.isDevOrTest(env)) {
		if (getEnvVar("BITE_PROXY") !== "1") {
			const localOverride = getEnvVar(serviceConfig.urlEnvVar);
			if (localOverride) return localOverride;
		}
		return `http://${getIpAddress()}:${serviceConfig.port}`;
	}
	if (getEnvVar("PORTER_APP_SERVICE_NAME") && !EnvironmentHelper.isPreview()) switch (env) {
		case Environment.PROD:
		case Environment.STAGING:
		case Environment.SANDBOX: {
			const porterApplication = serviceConfig.porterApplicationByEnv[env];
			if (!porterApplication) return;
			return `http://${porterApplication}-web.default.svc.cluster.local.:${serviceConfig.port}`;
		}
		case Environment.DEV:
		case Environment.TEST: return;
	}
}
/**
* @description This is a good centralized method to rewrite our asset urls if there's a need
*/
function biteAssetUrlWithNewHost(originalAssetUrl, newHost) {
	if (newHost && originalAssetUrl) {
		const host = originalAssetUrl.substring(0, originalAssetUrl.indexOf("/", originalAssetUrl.indexOf("://") + 3) + 1);
		const newHostWithSlash = newHost.endsWith("/") ? newHost : `${newHost}/`;
		return originalAssetUrl.replace(host, newHostWithSlash);
	}
	return originalAssetUrl;
}
function bureauUrl(env) {
	if (EnvironmentHelper.isPreview()) return `https://pr-${EnvironmentHelper.porterPrNumber()}-bureau.getbite.net`;
	switch (env) {
		case Environment.PROD: return "https://admin.getbite.com";
		case Environment.SANDBOX: return "https://admin-sandbox.getbite.com";
		case Environment.STAGING: return "https://admin-staging.getbite.com";
		case Environment.TEST:
		case Environment.DEV: {
			const override = getEnvVar("BUREAU_URL");
			if (override) return override;
			return `http://${getEnvVar("BUREAU_LOCALHOST") ?? getIpAddress()}:${BiteServicePorts.Bureau}`;
		}
	}
}
function webhookReceiverBaseUrl(env) {
	if (EnvironmentHelper.isPreview()) return `https://pr-${EnvironmentHelper.porterPrNumber()}-webhook-receiver.getbite.net`;
	switch (env) {
		case Environment.PROD: return "https://webhook.getbite.com";
		case Environment.STAGING: return "https://webhook-staging.getbite.com";
		case Environment.SANDBOX: return "https://webhook-sandbox.getbite.com";
		case Environment.DEV:
		case Environment.TEST: {
			const override = getEnvVar("WEBHOOK_RECEIVER_URL");
			if (override) return override;
			return `http://${getIpAddress()}:${BiteServicePorts.WebhookReceiver}`;
		}
	}
}
/** Read env var safely — works in both Node and browser contexts */
function getEnvVar(name) {
	return globalThis?.process?.env?.[name];
}
function getIpAddress() {
	return globalThis?.process?.env?.LOCAL_IP ?? "localhost";
}
//#endregion
export { biteApiUrl, biteAssetUrlWithNewHost, biteReceiptUrl, bite_url_exports, bureauUrl, internalServiceUrl, mcpBaseUrl, recommendationServiceUrl, resolveDeploymentServiceUrl, serviceUrl, webhookReceiverBaseUrl };

//# sourceMappingURL=bite_url.js.map